/** Rótulos e utilitários de calendário/treino (pt-BR). */
import type { CyclePhase, Quality, StepPhase, WorkoutStatus, WorkoutType } from "../api/types";
import { formatDuration, metersToKm } from "./format";
import { LOCALE, t } from "../i18n";

export const WORKOUT_TYPE_LABEL: Record<WorkoutType, string> = {
  easy: t("Leve / regenerativo"),
  continuous: t("Contínuo"),
  long_run: t("Rodagem longa"),
  intervals: t("Intervalado"),
  fartlek: t("Fartlek"),
  tempo: t("Ritmo / limiar"),
  track: t("Pista"),
  hills: t("Subidas"),
  test: t("Teste"),
  recovery: t("Recuperação"),
  strength: t("Fortalecimento"),
  custom: t("Personalizado"),
  rest: t("Descanso"),
};

/** Intensidade aproximada só para a cor do marcador do cartão (não é métrica). */
export const WORKOUT_TONE: Record<WorkoutType, "easy" | "steady" | "hard" | "other" | "rest"> = {
  easy: "easy",
  recovery: "easy",
  continuous: "steady",
  long_run: "steady",
  tempo: "hard",
  intervals: "hard",
  fartlek: "hard",
  track: "hard",
  hills: "hard",
  test: "hard",
  strength: "other",
  custom: "other",
  rest: "rest",
};

export const PHASE_LABEL: Record<StepPhase, string> = {
  warmup: t("Aquecimento"),
  active: t("Execução"),
  recovery: t("Recuperação"),
  cooldown: t("Desaquecimento"),
  rest: t("Pausa parada"),
  other: t("Outro"),
};

export const CYCLE_LABEL: Record<CyclePhase, string> = {
  base: t("Base"),
  development: t("Desenvolvimento"),
  specific: t("Especificidade"),
  taper: t("Polimento pré-prova"),
  recovery: t("Recuperação pós-prova"),
  custom: t("Personalizado"),
};

export const STATUS_WORKOUT_LABEL: Record<WorkoutStatus, string> = {
  planned: t("Planejado"),
  completed: t("Concluído"),
  skipped: t("Não realizado"),
  cancelled: t("Cancelado"),
};

/** Distância planejada com a qualidade do cálculo: "8,4 km", "≈ 8,4 km", "≥ 7,2 km". */
export function plannedDistance(m: number | null, quality: Quality | "manual"): string {
  if (m === null || quality === "unknown") return "";
  const prefix = quality === "estimated" ? "≈ " : quality === "partial" ? "≥ " : "";
  return prefix + metersToKm(m);
}

export function plannedDuration(s: number | null, quality: Quality): string {
  if (s === null || quality === "unknown") return "";
  const prefix = quality === "estimated" ? "≈ " : quality === "partial" ? "≥ " : "";
  return prefix + formatDuration(s);
}

export const QUALITY_HINT: Record<Quality | "manual", string> = {
  exact: t("Calculado pelos blocos."),
  estimated: t("Estimado a partir dos ritmos-alvo dos trechos por tempo."),
  partial: t("Mínimo conhecido: há trechos sem distância nem ritmo-alvo."),
  unknown: t("Sem dados suficientes para calcular."),
  manual: t("Definido manualmente pelo treinador."),
};

/* ------------------------------------------------------------------ datas (civis, sem fuso) */

export function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function parseIso(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Segunda-feira da semana de `d`. */
export function startOfWeek(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return addDays(x, -((x.getDay() + 6) % 7));
}

export function startOfMonthGrid(d: Date): Date {
  return startOfWeek(new Date(d.getFullYear(), d.getMonth(), 1));
}

export function todayIso(): string {
  return iso(new Date());
}

const dayFmt = new Intl.DateTimeFormat(LOCALE, { weekday: "short", day: "2-digit", month: "2-digit" });
const longFmt = new Intl.DateTimeFormat(LOCALE, { weekday: "long", day: "numeric", month: "long" });
const monthFmt = new Intl.DateTimeFormat(LOCALE, { month: "long", year: "numeric" });
const shortFmt = new Intl.DateTimeFormat(LOCALE, { day: "2-digit", month: "short" });

export const fmtDayHeader = (d: Date) => dayFmt.format(d).replace(".", "");
export const fmtLong = (s: string) => longFmt.format(parseIso(s));
export const fmtMonth = (d: Date) => {
  const tp = monthFmt.format(d);
  return tp.charAt(0).toUpperCase() + tp.slice(1);
};
export const fmtShort = (d: Date) => shortFmt.format(d).replace(".", "");

export function weekLabel(monday: Date): string {
  const sunday = addDays(monday, 6);
  return t("{a} a {b}", { a: fmtShort(monday), b: fmtShort(sunday) });
}
